import { buildImageUrl } from "@/sanity/lib/image"
import placeholder from "@assets/images/placeholder.png"
import type { SanityImageObject, SanityImageSource } from "@sanity/image-url"
import { globalSuffixPattern, lastSuffixPattern } from "./siteconfig/regexPatterns"

export const sanityBaseUrl =
    process.env.NEXT_SANITY_BASE_IMAGE_URL || "https://cdn.sanity.io/images/"

/**
 * ------------------------------------------------------------
 * Sanity → Bunny CDN rewrite
 * ------------------------------------------------------------
 * When NEXT_PUBLIC_SANITY_BUNNY_CDN_URL is set, the
 * `https://cdn.sanity.io/images/{projectId}/{dataset}/` prefix on
 * generated Sanity image URLs is rewritten to the Bunny pull-zone URL.
 * The Bunny pull zone is configured with origin scoped to the
 * project + dataset, so only the trailing asset path is preserved.
 */

const SANITY_BUNNY_CDN_URL = process.env.NEXT_PUBLIC_SANITY_BUNNY_CDN_URL
const SANITY_PROJECT_ID = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID
const SANITY_DATASET = process.env.NEXT_PUBLIC_SANITY_DATASET

const sanitySourcePrefix =
    SANITY_PROJECT_ID && SANITY_DATASET
        ? `https://cdn.sanity.io/images/${SANITY_PROJECT_ID}/${SANITY_DATASET}/`
        : null

export function transformSanityToBunnyCdn(url: string): string {
    if (!SANITY_BUNNY_CDN_URL || !sanitySourcePrefix) {
        return url
    }

    url += `${url.includes("?") ? "&" : "?"}v=2` // Hack to force reload of CMS images stuck in cache

    return url.replace(sanitySourcePrefix, SANITY_BUNNY_CDN_URL)
}

interface SanityLoaderProps {
    src: string
    width: number
    quality?: number
    rect?: number[]
    height?: number
}

/**
 * ------------------------------------------------------------
 * Bunny CDN configuration/transformations
 * ------------------------------------------------------------
 */

const BUNNY_CDN_CONFIG = {
    qa: {
        baseUrl: "https://poc-qa-images.b-cdn.net/",
        sourcePatterns: ["https://poc.centraqa.com/client/dynamic/images/"]
    },
    development: {
        baseUrl: "https://poc-qa-images.b-cdn.net/",
        sourcePatterns: ["https://poc.centraqa.com/client/dynamic/images/"]
    },
    production: {
        baseUrl: "https://poc-images.b-cdn.net/",
        sourcePatterns: ["https://poc.centracdn.net/client/dynamic/images/"]
    }
} as const

type Environment = "qa" | "production"

function getEnvironment(): Environment {
    const isProduction = process.env.NEXT_PUBLIC_ENVIRONMENT === "production"
    return isProduction ? "production" : "qa"
}

function getBunnyCdnConfig() {
    return BUNNY_CDN_CONFIG[getEnvironment()]
}

export function checkIfImageSrcIsCentraImage(src: string): boolean {
    const config = getBunnyCdnConfig()
    // Check if it's a source pattern (needs transformation)
    const isSourceUrl = config.sourcePatterns.some(pattern => src.includes(pattern))
    // Check if it's already a Bunny CDN URL (already transformed)
    const isBunnyCdnUrl = src.includes(config.baseUrl) || src.includes("b-cdn.net")
    return isSourceUrl || isBunnyCdnUrl
}

export function transformToBunnyCdn(url: string): string {
    if (!url || typeof url !== "string") {
        return url || ""
    }

    const config = getBunnyCdnConfig()

    if (!config.baseUrl) {
        return url
    }

    for (const pattern of config.sourcePatterns) {
        if (url.includes(pattern)) {
            return url.replace(pattern, config.baseUrl)
        }
    }

    return url
}

export function normalizeImageUrl(url: string): string {
    if (!url || typeof url !== "string") return url

    // Match extension
    const extensionMatch = url.match(/\.(jpg|jpeg|png|webp|gif)$/i)
    if (!extensionMatch) return url

    const extension = extensionMatch[0]
    const urlWithoutExt = url.slice(0, -extension.length)

    // Find all size suffixes in the URL
    const matches = [...urlWithoutExt.matchAll(globalSuffixPattern)]

    // Only remove if there are 2+ size suffixes (duplicates)
    if (matches.length < 2) {
        return url
    }

    // Remove only the last suffix
    return urlWithoutExt.replace(lastSuffixPattern, "") + extension
}

/**
 * ------------------------------------------------------------
 * Bunny CDN URL builder/loader
 * ------------------------------------------------------------
 */

export interface MediaLoaderProps {
    src: string
    width: number
    height?: number
    auto?: boolean
    quality: number
    fit?: string
    /**
     * Force a specific output format (maps to the backend `fm` param). Leave
     * undefined to let `auto=format` negotiate AVIF/WebP from the Accept header.
     * Set explicitly (e.g. "webp") for tiny thumbnails where AVIF's fixed
     * overhead makes it larger than WebP — but pair it with `auto: false`,
     * since the backend lets `auto=format` override an explicit `fm`.
     */
    format?: string
    background?: string
}

export const defaultMediaLoaderProps: Omit<MediaLoaderProps, "src" | "width"> = {
    quality: 80,
    fit: "",
    auto: true,
    height: undefined
}

export function buildBunnyUrl({
    src,
    width,
    ...mediaLoaderProps
}: Omit<MediaLoaderProps, "src" | "width"> & { src: string; width: number }): string {
    const { quality, fit, auto, height, format, background } = mediaLoaderProps
    if (!src || typeof src !== "string") {
        return ""
    }

    const params = new URLSearchParams()
    params.set("w", width.toString())
    params.set("q", quality.toString())
    if (auto) {
        params.set("auto", "format")
    }
    if (format) {
        params.set("fm", format)
    }
    if (height) {
        params.set("h", height.toString())
    }
    if (fit) {
        params.set("fit", fit)
    }
    if (background) {
        params.set("bg", background)
    }

    try {
        const urlObj = new URL(src)
        params.forEach((value, key) => {
            urlObj.searchParams.set(key, value)
        })
        return urlObj.toString()
    } catch {
        const separator = src.includes("?") ? "&" : "?"
        return `${src}${separator}${params.toString()}`
    }
}

export function bunnyLoader({
    src,
    width, // width is set by the next.config.js devices. Then the browser decides which to use in combination with sizes we set on images.
    auto = true,
    height,
    quality = 80,
    fit = "",
    format,
    background
}: MediaLoaderProps): string {
    if (!src || typeof src !== "string") {
        return placeholder.src
    }

    let url = src
    url = transformToBunnyCdn(url)
    url = normalizeImageUrl(url)
    url = buildBunnyUrl({
        src: url,
        width,
        auto,
        height,
        quality,
        fit,
        format,
        background
    })
    return url
}

/**
 * ------------------------------------------------------------
 * Sanity image loader
 * ------------------------------------------------------------
 * The sanityLoader is running as an export function of the sanityLoader.ts file It is going to take care of the sanity images that is loading.
 * The custom loaders is used as an override in the BaseImage component, if there's a override of the loader in the BaseImage component, it will use the custom loader instead of this below.
 *
 */

export function isSanityImageObject(
    src: SanityImageSource | string
): src is SanityImageObject {
    return typeof src === "object" && src !== null && "asset" in src && "crop" in src
}

const SANITY_REF_PATTERN = /^image-[a-f\d]+-(\d+)x\d+-\w+$/

/**
 * Parse the intrinsic width from a Sanity asset ref (e.g. "image-abc123-1125x750-jpg" → 1125).
 * Returns undefined if the ref doesn't match the expected format.
 */
export function getIntrinsicWidth(src: string): number | undefined {
    const match = SANITY_REF_PATTERN.exec(src)
    return match ? parseInt(match[1], 10) : undefined
}

export default function sanityLoader({ src, width, quality }: SanityLoaderProps): string {
    const isSanityMedia =
        (typeof src === "string" && src.startsWith(sanityBaseUrl)) ||
        src.startsWith("image-")

    if (!isSanityMedia) {
        return src
    }
    const intrinsicWidth = typeof src === "string" ? getIntrinsicWidth(src) : undefined
    const cappedWidth = intrinsicWidth ? Math.min(width, intrinsicWidth) : width

    const fullImageObject = isSanityImageObject(src) ? src : null

    // Use the stored full image object if available
    if (fullImageObject) {
        // Check if there's crop data & not null
        const hasCropData =
            fullImageObject.crop &&
            (fullImageObject.crop.left !== 0 ||
                fullImageObject.crop.right !== 0 ||
                fullImageObject.crop.top !== 0 ||
                fullImageObject.crop.bottom !== 0)

        // Check if there's hotspot data & not null
        const hasHotspotData =
            fullImageObject.hotspot &&
            (fullImageObject.hotspot.x !== 0 ||
                fullImageObject.hotspot.y !== 0 ||
                fullImageObject.hotspot.width !== 0 ||
                fullImageObject.hotspot.height !== 0)

        // Use crop if there's either crop data OR hotspot data
        const shouldUseCrop = hasCropData || hasHotspotData
        const builtUrl =
            buildImageUrl(fullImageObject)
                .width(cappedWidth)
                .auto("format")
                .quality(quality ?? 90)
                .fit(shouldUseCrop ? "crop" : "clip")
                .url() ?? ""

        return transformSanityToBunnyCdn(builtUrl)
    }

    const assetId = src.split("?")[0]
    let url =
        buildImageUrl(assetId)
            .width(cappedWidth)
            .auto("format")
            .quality(quality ?? 90)
            .fit("clip")
            .url() ?? ""

    if (src.endsWith(".gif") || src.endsWith("-gif")) {
        url += `&h=${Math.round(Math.round(cappedWidth / 2.5))}`
    }

    return transformSanityToBunnyCdn(url)
}

// Old loader keep for now.
// export default function sanityLoader({
//     src,
//     width,
//     height,
//     quality
// }: SanityLoaderProps): string {
//     const isSanityMedia =
//         (typeof src === "string" && src.startsWith(sanityBaseUrl)) ||
//         src.startsWith("image-")

//     if (!isSanityMedia) {
//         return src
//     }

//     // Remove query parameters from the asset id
//     const assetId = src.split("?")[0]

//     let url = buildImageUrl(assetId).width(width).auto("format").fit("clip").url() ?? ""

//     if (src.endsWith(".gif")) {
//         url += `&h=${Math.round(IMAGE_MOBILE_SIZE_PX / 2.5)}`
//     }
//     if (height) {
//         url += `&h=${height}`
//     }
//     if (quality) {
//         url += `&q=${quality}`
//     }

//     return url
// }
